import {useEffect, useState} from 'react'
import PageHeader from '@components/page-header'
import {Params, useFetchGet} from 'helpers/api'
import {capitalizeFirstLetter} from 'helpers/style'
import {RailGatewayStatus} from './rail-gateway-status'
import GatewayAlerts from './gateway-alerts'
import {ErrorState} from '@components/error-state'
import {LoadingState} from '@components/loading-state'
import GatewayTargets from './gateway-targets'
import {useParams} from 'react-router-dom'
import MetricsCards from '../metrics-cards'
import RailgwMapRow from './railgw-map-row'
import EventAnalysisSection from './event-analysis-section'
import {useDomain} from 'contexts/DomainContext'

function getTitle(gatewaysview, terms) {
  if (!gatewaysview) return ''
  return (
    <>
      <span className="text-gray-500">{terms.site}:</span> {gatewaysview.s_name}{' '}
      <span className="text-gray-500">{terms.gateway}:</span> {gatewaysview.g_name}
    </>
  )
}

function getExtra(gatewaysview) {
  if (!gatewaysview) return ''
  return (
    <>
      {gatewaysview.g_macid ? `MAC ID: ${gatewaysview.g_macid}` : ''}
      {gatewaysview.g_macid && gatewaysview.g_imei ? ' ' : ''}
      {gatewaysview.g_imei ? `IMEI: ${gatewaysview.g_imei}` : ''}
    </>
  )
}

const GatewayDashboard = () => {
  const {gid} = useParams()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [gatewaysview, setGatewaysview] = useState(null)
  const fetchGatewaysView = useFetchGet('gatewaysview')
  const [targetsview, setTargetsview] = useState(null)
  const fetchTargetsview = useFetchGet('targetsview')
  const {domain, terms} = useDomain()

  const fetchGatewayData = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const params = Params({g_id: gid, limit: 1})
      await fetchGatewaysView(params, response => {
        if (!response?.gatewaysview?.length) {
          throw new Error('Gateway not found')
        }
        setGatewaysview(response.gatewaysview[0])
      })
    } catch (err) {
      setError(err.message || 'Failed to fetch gateway data')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchTargetData = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const params = Params({g_id: gid, limit: 1})
      await fetchTargetsview(params, response => {
        if (!response?.targetsview?.length) {
          throw new Error('Target not found')
        }
        setTargetsview(response.targetsview[0])
      })
    } catch (err) {
      setError(err.message || 'Failed to fetch target data')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (gid) {
      fetchGatewayData()
      fetchTargetData()
      window.scrollTo({top: 0, behavior: 'smooth'})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gid])

  if (isLoading) {
    return <LoadingState />
  }

  if (error) {
    return <ErrorState error={error} />
  }

  if (!gatewaysview) {
    return null
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <div style={{padding: '0 1rem'}}>
        <PageHeader
          ghost={false}
          onBack={() => window.history.back()}
          title={getTitle(gatewaysview, terms)}
          extra={getExtra(gatewaysview)}
        />
      </div>

      {domain !== 'rail' && <MetricsCards query={{g_id: gatewaysview.g_id}} />}

      {domain === 'rail' && <EventAnalysisSection gatewaysview={gatewaysview} targetsview={targetsview} />}

      <GatewayAlerts gatewaysview={gatewaysview} />

      {domain === 'rail' && <RailgwMapRow gatewayId={gatewaysview.g_id} handleGatewayClick={() => {}} />}

      <GatewayTargets gatewaysview={gatewaysview} />

      {domain === 'rail' && <RailGatewayStatus query={{gid: gatewaysview.g_id}} />}
    </main>
  )
}

export default GatewayDashboard
